import { colors } from '@packages/core/styles';
import { Button, Icon, type StsIconName, Typography } from '@packages/ui/shared';
import React, { useCallback } from 'react';
import { createUseStyles } from 'react-jss';

const SECTION_PADDING = 24;

const useStyles = createUseStyles({
    root: { display: 'block' },
    divider: {
        border: 0,
        borderTop: `1px solid ${colors.grayThree}`,
        margin: 0,
    },
    row: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        padding: `${SECTION_PADDING}px 0`,
        gap: 12,
    },
    left: {
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
    },
    right: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },
    content: {
        paddingBottom: SECTION_PADDING,
    },
    toggleBtnReset: {
        padding: 0,
        minWidth: 0,
    },
});

/**
 * Props for the {@link CollapsibleSection} component.
 */
export type CollapsibleSectionProps = {
    /** The content rendered inside the collapsible body. */
    children?: React.ReactNode;

    /** Optional icon name displayed to the right of the title. */
    iconName?: StsIconName;

    /** Additional CSS class names applied to the root section element. */
    className?: string;

    /** Duration in ms for the collapse/expand transition. */
    collapseDuration?: number;

    /** Whether the section can be collapsed at all. When false, the toggle button is hidden. */
    collapsible?: boolean;

    /** Initial expanded state for uncontrolled usage. */
    defaultExpanded?: boolean;

    /** Controlled expanded state. When provided, the component becomes controlled. */
    expanded?: boolean;

    /** HTML id prefix for the content region and heading. */
    id?: string;

    /** Callback invoked when the expanded state changes. */
    onExpandedChange?: (expanded: boolean) => void;

    /** Optional action element rendered to the right of the title. */
    rightAction?: React.ReactNode;

    /** Whether to show a divider line below the section. */
    showDividerBottom?: boolean;

    /** Whether to show a divider line above the section. */
    showDividerTop?: boolean;

    /** Whether to show the right action element. */
    showRightAction?: boolean;

    /** Optional subtitle displayed to the right of the title in a lighter style. */
    subtitle?: string;

    /** The section title, rendered as text or a custom ReactNode. */
    title: React.ReactNode;

    /** Custom aria-label for the collapse/expand toggle button. */
    toggleAriaLabel?: string;
};

/**
 * A section with a title bar and an optional collapsible content area. Supports both
 * controlled and uncontrolled expanded state, optional top/bottom dividers, and a slot
 * for a right-side action element next to the title.
 *
 * @param props - {@link CollapsibleSectionProps}
 * @returns the collapsible section JSX
 */
export function CollapsibleSection({
    title,
    iconName,
    children,
    rightAction,
    collapsible = true,
    defaultExpanded = false,
    showDividerTop = true,
    showDividerBottom = false,
    showRightAction = true,
    subtitle,
    className,
    expanded: expandedProp,
    onExpandedChange,
    toggleAriaLabel,
    id,
    collapseDuration: _collapseDuration = 300,
}: CollapsibleSectionProps) {
    const classes = useStyles();
    const generatedId = React.useId();
    const contentId = `${id ?? generatedId}-content`;

    const isControlled = typeof expandedProp === 'boolean';
    const [expandedUncontrolled, setExpandedUncontrolled] = React.useState<boolean>(defaultExpanded);
    const expanded = isControlled ? (expandedProp as boolean) : expandedUncontrolled;

    const hasContent = Boolean(children);
    const showToggle = collapsible && hasContent;

    /** Toggle the expanded/collapsed state and notify the parent via callback. */
    const handleToggle = useCallback(() => {
        const next = !expanded;
        if (!isControlled) {
            setExpandedUncontrolled(next);
        }
        onExpandedChange?.(next);
    }, [expanded, isControlled, onExpandedChange]);

    const renderTitle =
        typeof title === 'string' ? (
            <div className="d-flex align-items-center gap-3">
                <Typography variant="h3">{title}</Typography>
                {iconName && <Icon color="redOne" name={iconName} size={20} />}
                {subtitle && (
                    <div>
                        <Typography color="textDarkSecondary" variant="labelRegular">
                            {subtitle}
                        </Typography>
                    </div>
                )}
            </div>
        ) : (
            title
        );

    return (
        <section aria-labelledby={`${contentId}-heading`} className={`${classes.root} ${className ?? ''}`}>
            {showDividerTop && <hr className={classes.divider} />}

            <div className={classes.row}>
                <div className={classes.left} id={`${contentId}-heading`}>
                    {renderTitle}
                </div>
                <div className={classes.right}>
                    {showRightAction && rightAction}
                    {showToggle && (
                        <Button
                            aria-controls={contentId}
                            aria-expanded={expanded}
                            aria-label={toggleAriaLabel ?? (expanded ? 'Collapse section' : 'Expand section')}
                            onPress={handleToggle}
                            title={toggleAriaLabel ?? (expanded ? 'Collapse' : 'Expand')}
                            variant="link"
                        >
                            <Icon color="blueOne" name={expanded ? 'chevron-up' : 'chevron-down'} size={20} />
                        </Button>
                    )}
                </div>
            </div>

            {hasContent && expanded && (
                <div aria-labelledby={`${contentId}-heading`} className={classes.content} id={contentId} role="region">
                    {children}
                </div>
            )}

            {showDividerBottom && <hr className={classes.divider} />}
        </section>
    );
}
